import tbkApi from "../../services/tbkApi";
import {
  LOAD_PROMOTIONS,
  LOAD_LOCATIONS,
  REPLACE_LOCATIONS,
} from "./App.actions";
import store from "../../services/ReduxStore";

/**
 * A dispatchable function that knows how to fetch resources from the server.  props is an object that can contain
 * pagination, sorting, filter and search members.
 *
 * @param {Object} props
 *
 * @returns {function(*)}
 */
export const fetchPromotions = () => {
  return (dispatch) => {
    tbkApi.getPromotions().then((promotions) => {
      dispatch({
        type: LOAD_PROMOTIONS,
        promotions,
      });
    });
  };
};

function toInt(value) {
  const n = parseInt(value, 10);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function extractId(value) {
  if (value == null || value === "" || value === 0 || value === "0") {
    return null;
  }

  if (Array.isArray(value)) {
    return extractId(value[0]);
  }

  if (typeof value === "object") {
    return toInt(value.id) || toInt(value.ID) || null;
  }

  return toInt(value);
}

function normalizeIdArray(value) {
  let arr = value;

  if (arr == null || arr === "") {
    return [];
  }

  if (typeof arr === "string") {
    arr = arr.includes(",") ? arr.split(",") : [arr];
  }

  if (!Array.isArray(arr)) {
    arr = [arr];
  }

  return [...new Set(arr.map(extractId).filter(Boolean))];
}

function hydratePendingFromLive(location) {
  if (!location) {
    location = {};
  }

  var livePromos = normalizeIdArray(location.active_promotions);
  var pendingPromos = normalizeIdArray(location.pending_promotions);

  var liveBoxA = extractId(location.promo_box_a);
  var liveBoxB = extractId(location.promo_box_b);
  var liveBoxC = extractId(location.promo_box_c);
  var liveBoxAlert = extractId(location.promo_box_alert);

  var pendingBoxA = extractId(location.pending_box_a);
  var pendingBoxB = extractId(location.pending_box_b);
  var pendingBoxC = extractId(location.pending_box_c);
  var pendingBoxAlert = extractId(location.pending_box_alert);

  return {
    ...location,
    ID: extractId(location.ID || location.id),
    id: extractId(location.id || location.ID),

    promo_box_a: liveBoxA,
    promo_box_b: liveBoxB,
    promo_box_c: liveBoxC,
    promo_box_alert: liveBoxAlert,

    pending_box_a: pendingBoxA !== null ? pendingBoxA : liveBoxA,
    pending_box_b: pendingBoxB !== null ? pendingBoxB : liveBoxB,
    pending_box_c: pendingBoxC !== null ? pendingBoxC : liveBoxC,
    pending_box_alert:
      pendingBoxAlert !== null ? pendingBoxAlert : liveBoxAlert,

    active_promotions: livePromos,
    pending_promotions: pendingPromos.length ? pendingPromos : livePromos,
  };
}

function hydrateLocationsPendingFromLive(locations) {
  if (!locations) {
    locations = [];
  }

  return locations.map(hydratePendingFromLive);
}

export const fetchLocations = function(locations) {
  if (typeof locations === "undefined") {
    locations = null;
  }

  return function(dispatch) {
    tbkApi.getLocations().then(function(stateLocations) {
      var hydratedLocations = hydrateLocationsPendingFromLive(stateLocations);

      if (locations && locations.length) {
        var ids = new Set(
          locations
            .map(function(loc) {
              return Number(loc.ID || loc.id);
            })
            .filter(Boolean),
        );

        var locationArray = hydratedLocations.filter(function(loc) {
          return ids.has(Number(loc.ID || loc.id));
        });

        dispatch({
          type: REPLACE_LOCATIONS,
          locations: locationArray,
        });
      } else {
        dispatch({
          type: LOAD_LOCATIONS,
          locations: hydratedLocations,
        });
      }
    });
  };
};

const _getPendingPayload = function(loc) {
  if (!loc) {
    loc = [];
  }

  var payload = {};
  var stateLocations =
    loc && loc.length ? loc : store.getState().app.locations || [];

  var selectedIds = loc.length
    ? new Set(
        loc.map(function(item) {
          return Number(item.ID || item.id);
        }),
      )
    : null;

  stateLocations.forEach(function(location) {
    var locationId = Number(location.ID || location.id);

    if (selectedIds && !selectedIds.has(locationId)) {
      return;
    }

    payload[locationId] = {
      promo_box_a: location.pending_box_a
        ? Number(location.pending_box_a)
        : null,
      promo_box_b: location.pending_box_b
        ? Number(location.pending_box_b)
        : null,
      promo_box_c: location.pending_box_c
        ? Number(location.pending_box_c)
        : null,
      promo_box_alert: location.pending_box_alert
        ? Number(location.pending_box_alert)
        : null,
      active_promotions: Array.isArray(location.pending_promotions)
        ? Array.from(
            new Set(
              location.pending_promotions.map(Number).filter(function(id) {
                return id > 0;
              }),
            ),
          )
        : [],
    };
  });

  return payload;
};

export const saveDraftPromotions = (locations = []) => {
  return (dispatch) => {
    const hydratedLocations = hydrateLocationsPendingFromLive(locations);

    tbkApi
      .savePendingPromotions(_getPendingPayload(hydratedLocations))
      .then(() => {
        dispatch(
          fetchLocations(
            hydratedLocations.length > 0 ? hydratedLocations : null,
          ),
        );
      });
  };
};

export const publishToLive = (locations = []) => {
  return (dispatch) => {
    tbkApi.savePendingPromotions(_getPendingPayload(locations)).then(() => {
      tbkApi
        .publishPendingToLive(locations.length > 0 ? locations : null)
        .then(() => {
          dispatch(fetchLocations(locations.length > 0 ? locations : null));
        });
    });
  };
};

export const createLookupMap = (collection) => {
  let lookup = {};
  collection.forEach((item, index) => {
    lookup[item.ID] = index;
  });
  return lookup;
};

export const lookup = (key, ID) => {
  const collection = store.getState().app[key];
  const lookupMap = store.getState().app.lookups[key];
  return ID && collection && lookupMap && typeof lookupMap[ID] !== "undefined"
    ? collection[lookupMap[ID]]
    : null;
};

export const getPromoActiveCount = (promo, status = "live") => {
  if (!promo || !promo.ID) {
    return 0;
  }

  const promoId = Number(promo.ID);
  const locations = store.getState().app.locations || [];

  return locations.filter((location) => {
    const boxA = Number(
      status === "live" ? location.promo_box_a : location.pending_box_a,
    );
    const boxB = Number(
      status === "live" ? location.promo_box_b : location.pending_box_b,
    );
    const boxC = Number(
      status === "live" ? location.promo_box_c : location.pending_box_c,
    );
    const boxAlert = Number(
      status === "live" ? location.promo_box_alert : location.pending_box_alert,
    );

    const active =
      status === "live"
        ? location.active_promotions || []
        : location.pending_promotions || [];

    const activeIds = active.map(Number);

    return (
      boxA === promoId ||
      boxB === promoId ||
      boxC === promoId ||
      boxAlert === promoId ||
      activeIds.includes(promoId)
    );
  }).length;
};
